import { Redirect } from '../constants/types';
import { AxiosHelper } from '../helpers/axios.helper';
import { apiRequest } from './fetcher';

interface LoginParams {
  nickNameOrEmail: string;
  password: string;
}

type LoginOTPParams = LoginParams & {
  oneTimePassword: number;
};

interface CreateUserFromSkemeetAndLoginParams {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  cellphone: string;
}

interface Login {
  success: boolean;
  oneTimePasswordActivated: boolean;
  doubleAuthEmailActivated: boolean;
  message: string;
  accessToken: string;
  refreshToken: string;
  changeSkemeetPassword?: boolean;
  firstname?: string;
  lastname?: string;
  cellphone?: string;
  temporaryAccessToken?: string;
}

export default class AuthService {
  async createUserFromSkemeetAndLogin(data: CreateUserFromSkemeetAndLoginParams): Promise<void> {
    await apiRequest.post(`/auth/create-user-from-skemeet-and-login`, data);
  }

  async login(login: LoginParams, resendCode: boolean): Promise<Login & Redirect> {
    const response = await apiRequest.post<Login>(`/auth/login`, login, { headers: { 'Skezi-Rensend-Code': String(resendCode) } });
    const data: Login & Redirect = { ...response.data };
    return AxiosHelper.addRedirectUrlFromHeader(data, response.headers['skezi-redirect']);
  }

  async logout(): Promise<void> {
    await apiRequest.post(`/auth/logout`, {});
  }

  async checkOneTimePassword(loginOTP: LoginOTPParams): Promise<void> {
    await apiRequest.post(`/auth/check-one-time-password`, loginOTP);
  }

  async getProSanteConnectLink(): Promise<{ authenticationUrl: string }> {
    return (await apiRequest.get(`/auth/pro-sante-connect/connexion`)).data;
  }

  async proSanteConnectCheckUserInfo(code: string): Promise<{
    success: boolean;
    message: string;
    hasEmail: boolean;
    hasEmailConfirmed: boolean;
    appConnectEmail: string | null;
    accessToken: string;
    refreshToken: string;
    askMergeAccount: boolean;
    appToken: string;
    idToken: string;
  }> {
    return (await apiRequest.post(`/auth/pro-sante-connect/checkUserInfo`, { code })).data;
  }

  async getFranceConnectLink(): Promise<{ authenticationUrl: string }> {
    return (await apiRequest.get(`/auth/france-connect/connexion`)).data;
  }

  async getFranceConnectLogoutLink(idToken: string): Promise<{ logoutUrl: string }> {
    return (await apiRequest.get(`/auth/france-connect/logout/${idToken}`)).data;
  }

  async franceConnectCheckUserInfo(code: string): Promise<{
    success: boolean;
    message: string;
    hasEmail: boolean;
    hasEmailConfirmed: boolean;
    appConnectEmail: string | null;
    accessToken: string;
    refreshToken: string;
    askMergeAccount: boolean;
    appToken: string;
    idToken: string;
  }> {
    return (await apiRequest.post(`/auth/france-connect/checkUserInfo`, { code })).data;
  }

  async fcPscCreateAccount(): Promise<{
    success: boolean;
    accessToken: string;
    refreshToken: string;
  }> {
    return (await apiRequest.post(`/auth/FC-PSC/createAccount`)).data;
  }

  async fcPscMergeAccount(appToken?: string): Promise<{
    success: boolean;
    alreadyMerged?: boolean;
  }> {
    return (await apiRequest.post(`/auth/FC-PSC/mergeAccount`, {}, { params: { token: appToken } })).data;
  }

  async resetNickname(email: string): Promise<void> {
    return apiRequest.post(`/auth/reset-nickname`, { email, locationUrl: window.location.href });
  }

  async resetNicknameConfirm(token: string, nickName: string): Promise<void> {
    return apiRequest.post(`/auth/reset-nickname-confirm`, { token, nickName });
  }

  async resetPassword(email: string): Promise<void> {
    return apiRequest.post(`/auth/reset-password`, { email, locationUrl: window.location.href });
  }

  async resetPasswordConfirm(token: string, password: string): Promise<void> {
    return apiRequest.post(`/auth/reset-password-confirm`, { token, password });
  }
}
